import { CommonModule } from '@angular/common';
import {
  Component,
  Inject,
  inject,
  signal,
  computed,
  effect,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { HorusLoadingComponent } from '@shared/components/domain/horus-loading/horus-loading.component';
import { HorusMessageGeneralComponent } from '@shared/components/domain/horus-message-general/horus-message-general.component';
import { SearchState } from '@features/search/services/search.state';
import { InvoiceService } from '../../services/invoice.service';
import { Invoice } from '../../models/invoice.models';

export interface InvoiceResendEmailModalData {
  invoice: Invoice;
}

@Component({
  selector: 'app-invoice-resend-email-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    HorusLoadingComponent,
    HorusMessageGeneralComponent,
  ],
  templateUrl: './invoice-resend-email-modal.component.html',
  styleUrl: './invoice-resend-email-modal.component.scss',
})
export class InvoiceResendEmailModalComponent {
  private dialogRef = inject(MatDialogRef<InvoiceResendEmailModalComponent>);
  private searchState = inject(SearchState);
  private invoiceService = inject(InvoiceService);

  sendingState = signal<'idle' | 'sending' | 'success' | 'error'>('idle');

  isSending = computed(() => this.sendingState() === 'sending');
  isSuccess = computed(() => this.sendingState() === 'success');
  isError = computed(() => this.sendingState() === 'error');
  isIdle = computed(() => this.sendingState() === 'idle');

  clientEmail = computed(() => {
    const clientData = this.searchState.clientData();
    return clientData?.email || '';
  });

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      data: InvoiceResendEmailModalData;
    }
  ) {}

  sendEmail() {
    const email = this.clientEmail();

    if (!email) {
      this.sendingState.set('error');
      return;
    }

    this.sendingState.set('sending');

    this.invoiceService.resendEmail(this.data.data.invoice, email).subscribe({
      next: () => {
        this.sendingState.set('success');
      },
      error: (error) => {
        console.error('Error sending email:', error);
        this.sendingState.set('error');
      },
    });
  }

  retry() {
    this.sendingState.set('idle');
  }

  close() {
    this.dialogRef.close(false);
  }

  closeSuccess() {
    this.dialogRef.close(true);
  }
}
